import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, Envelope, MapPin, WhatsappLogo, InstagramLogo, FacebookLogo, Clock } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      toast.success('Message sent! We will get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        {/* Header */}
        <div className="mb-16">
          <p className="text-sm uppercase tracking-[0.2em] text-slate-400 mb-4" data-testid="contact-overline">Get in Touch</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium leading-none text-foreground mb-6" data-testid="contact-heading">
            Let's Work<br />
            <span className="text-[#D4AF37]">Together</span>
          </h1>
          <p className="text-base leading-relaxed text-slate-300 max-w-2xl" data-testid="contact-description">
            Have a bulk order inquiry? Want custom bottles for your event? Reach out to us and our team will help you create the perfect custom-labeled water bottles.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Info Cards */}
          <div className="lg:col-span-5 space-y-6">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="bg-card border border-border p-8" data-testid="contact-info-card">
              <h3 className="text-lg font-heading font-normal mb-6 text-foreground">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4" data-testid="contact-phone">
                  <div className="bg-[#D4AF37]/10 p-2.5 rounded-sm">
                    <Phone size={20} weight="regular" className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Phone / WhatsApp</p>
                    <p className="text-sm text-slate-400">+91 85498 80305</p>
                    <p className="text-sm text-slate-400">+91 79758 78923</p>
                    <p className="text-xs text-slate-500 mt-1">Call or WhatsApp for quick inquiries</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-email">
                  <div className="bg-[#D4AF37]/10 p-2.5 rounded-sm">
                    <Envelope size={20} weight="regular" className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Email</p>
                    <p className="text-sm text-slate-400">truedroplet@gmail.com</p>
                    <p className="text-xs text-slate-500 mt-1">We reply within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-address">
                  <div className="bg-[#D4AF37]/10 p-2.5 rounded-sm">
                    <MapPin size={20} weight="regular" className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Office Address</p>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      Anil Kumar A<br />
                      Tamalihalli, Jagalur,<br />
                      Davangere, Karnataka 577528, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-hours">
                  <div className="bg-[#D4AF37]/10 p-2.5 rounded-sm">
                    <Clock size={20} weight="regular" className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Business Hours</p>
                    <p className="text-sm text-slate-400">Monday - Saturday: 9:00 AM - 7:00 PM</p>
                    <p className="text-sm text-slate-400">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
              className="bg-card border border-border p-8" data-testid="social-card">
              <h3 className="text-lg font-heading font-normal mb-4 text-foreground">Follow Us</h3>
              <div className="flex space-x-4">
                <a href="https://www.instagram.com/truedroplet?igsh=bzUxZGp4MGZrM2hh" target="_blank" rel="noopener noreferrer"
                   className="flex items-center space-x-2 bg-secondary px-4 py-2.5 hover:bg-[#D4AF37]/20 transition-colors" data-testid="social-ig-link">
                  <InstagramLogo size={20} weight="regular" className="text-[#D4AF37]" />
                  <span className="text-sm text-slate-300">Instagram</span>
                </a>
                <a href="https://www.facebook.com/share/1bnH6954Z8/" target="_blank" rel="noopener noreferrer"
                   className="flex items-center space-x-2 bg-secondary px-4 py-2.5 hover:bg-[#D4AF37]/20 transition-colors" data-testid="social-fb-link">
                  <FacebookLogo size={20} weight="regular" className="text-[#D4AF37]" />
                  <span className="text-sm text-slate-300">Facebook</span>
                </a>
                <a href="https://wa.me/918549880305" target="_blank" rel="noopener noreferrer"
                   className="flex items-center space-x-2 bg-secondary px-4 py-2.5 hover:bg-[#D4AF37]/20 transition-colors" data-testid="social-wa-link">
                  <WhatsappLogo size={20} weight="regular" className="text-[#D4AF37]" />
                  <span className="text-sm text-slate-300">WhatsApp</span>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
              <form onSubmit={handleSubmit} className="bg-card border border-border p-8" data-testid="contact-form">
                <h3 className="text-lg font-heading font-normal mb-6 text-foreground">Send Us a Message</h3>
                
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="contact-name-input" placeholder="Your name" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="contact-phone-input" placeholder="+91 XXXXX XXXXX" />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="contact-email-input" placeholder="your@email.com" />
                  </div>
                  
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required data-testid="contact-subject-input" placeholder="e.g., Bulk Order Inquiry, Wedding Bottles" />
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="flex w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 focus:ring-offset-background"
                      placeholder="Tell us about your requirements - quantity, event type, customization needs..."
                      data-testid="contact-message-input"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full rounded-sm bg-[#D4AF37] hover:bg-[#D4AF37]/90 transition-transform duration-300 hover:-translate-y-1" disabled={submitting} data-testid="contact-submit-button">
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
